import { ReactNode } from "react";
import {
  AppBar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  LinearProgress,
  Toolbar,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useBranding } from "../../branding/BrandingProvider";
import { Copyright } from "../Layout/Copyright";
import { translucentSurface } from "../../theme/surfaces";

interface EnrollmentShellProps {
  /** Zero-based; null while loading. */
  step: number | null;
  steps: string[];
  firstName?: string;
  isTest: boolean;
  onSignOut: () => void;
  children: ReactNode;
}

/**
 * The frame around enrollment: a white app bar with the logo and Sign out, a thin progress bar,
 * and a narrow centered column. Deliberately calmer than the app shell — no navigation, one
 * decision per screen — because a patient here has not joined yet and the only ways out are
 * finishing or signing out.
 */
export default function EnrollmentShell({ step, steps, firstName, isTest, onSignOut, children }: EnrollmentShellProps) {
  const brand = useBranding();
  const progress = step === null ? 0 : ((step + 1) / steps.length) * 100;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <AppBar position="sticky" elevation={0} color="inherit" sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider" }}>
        {isTest && (
          <Box sx={{ bgcolor: "warning.light", color: "warning.contrastText", textAlign: "center", py: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              TEST environment
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                {" "}— documents use synthetic values and are not for patient use
              </Box>
              <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                {" "}— not for patient use
              </Box>
            </Typography>
          </Box>
        )}
        <Toolbar sx={{ gap: 2, minHeight: { xs: 60, sm: 68 } }}>
          <Box component="img" src={brand.logoSmall} alt={`${brand.displayName}`} sx={{ height: { xs: 36, sm: 44 } }} />
          <Box sx={{ flex: 1 }} />
          {firstName && (
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
              Signed in as {firstName}
            </Typography>
          )}
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={onSignOut} sx={{ textTransform: "none" }}>
            Sign out
          </Button>
        </Toolbar>
        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label="Enrollment progress"
          sx={{ height: 4, bgcolor: "action.hover", "& .MuiLinearProgress-bar": { transition: "transform .4s ease" } }}
        />
      </AppBar>

      <Container maxWidth="sm" sx={{ flex: 1, py: { xs: 3, sm: 6 }, px: { xs: 2, sm: 3 } }}>
        {step !== null && (
          <Typography
            variant="overline"
            sx={{ color: "primary.dark", fontWeight: 700, letterSpacing: 1.2, display: "block", mb: 1 }}
          >
            Step {step + 1} of {steps.length} · {steps[step]}
          </Typography>
        )}
        {children}
      </Container>

      <Box sx={{ py: 3 }}>
        <Copyright />
      </Box>
    </Box>
  );
}

/** A step's title block: one large question-style heading and a short supporting line. */
export function StepHeading({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, fontSize: { xs: "1.6rem", sm: "2rem" }, lineHeight: 1.2, mb: 1 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body1" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The primary action. Full-width and pinned to the bottom of the viewport on phones, where the
 * thumb is; inline on larger screens.
 */
export function StepActions({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        mt: 4,
        position: { xs: "sticky", sm: "static" },
        bottom: 0,
        mx: { xs: -2, sm: 0 },
        px: { xs: 2, sm: 0 },
        py: { xs: 1.5, sm: 0 },
        bgcolor: { xs: "background.default", sm: "transparent" },
        borderTop: { xs: 1, sm: 0 },
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        zIndex: 1,
      }}
    >
      {children}
    </Box>
  );
}

/** The house style for the step's main button. */
export const primaryButtonSx = {
  borderRadius: 999,
  py: 1.5,
  fontSize: "1rem",
  fontWeight: 700,
  textTransform: "none",
  boxShadow: "none",
} as const;

/** "← Back" above a step's heading. Earlier answers can be changed until the documents are signed. */
export function BackLink({ onClick, label = "Back" }: { onClick: () => void; label?: string }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<ArrowBackIcon />}
      color="inherit"
      sx={{ ml: -1, mb: 1, textTransform: "none", fontWeight: 600, color: "text.secondary" }}
    >
      {label}
    </Button>
  );
}

/**
 * Covers the whole page while a slow step runs (signing, preparing documents, opening checkout),
 * so the patient sees that something is happening and cannot click twice. Announced to screen
 * readers through role="status".
 */
export function BusyOverlay({ open, message }: { open: boolean; message: string }) {
  return (
    <Backdrop
      open={open}
      sx={(theme) => ({
        zIndex: theme.zIndex.modal + 1,
        flexDirection: "column",
        gap: 2,
        color: "text.primary",
        bgcolor: translucentSurface(theme, 0.85),
        backdropFilter: "blur(2px)",
      })}
    >
      <CircularProgress size={48} />
      <Typography role="status" aria-live="polite" sx={{ fontWeight: 600 }}>
        {open ? message : ""}
      </Typography>
    </Backdrop>
  );
}
